/**
 * Firebase Service
 *
 * Handles Firebase initialization, authentication, and Firestore operations
 */

import { initializeApp, FirebaseApp } from 'firebase/app';
import {
  getAuth,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  Auth,
  User
} from 'firebase/auth';
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  Firestore
} from 'firebase/firestore';
import type { FirebaseConfig, UserData } from '../types';

let app: FirebaseApp;
let auth: Auth;
let db: Firestore;

/**
 * Initialize Firebase with configuration
 */
export function initializeFirebase(config: FirebaseConfig): void {
  app = initializeApp(config);
  auth = getAuth(app);
  db = getFirestore(app);
}

/**
 * Get Firebase Auth instance
 */
export function getFirebaseAuth(): Auth {
  if (!auth) {
    throw new Error('Firebase not initialized. Call initializeFirebase() first.');
  }
  return auth;
}

/**
 * Get Firestore instance
 */
export function getFirebaseDb(): Firestore {
  if (!db) {
    throw new Error('Firebase not initialized. Call initializeFirebase() first.');
  }
  return db;
}

/**
 * Sign in with email and password
 */
export async function signIn(email: string, password: string): Promise<User> {
  const userCredential = await signInWithEmailAndPassword(auth, email, password);
  return userCredential.user;
}

/**
 * Sign out current user
 */
export async function logout(): Promise<void> {
  await signOut(auth);
}

/**
 * Subscribe to authentication state changes
 */
export function onAuthChange(callback: (user: User | null) => void): () => void {
  return onAuthStateChanged(auth, callback);
}

/**
 * Get current authenticated user
 */
export function getCurrentUser(): User | null {
  return auth.currentUser;
}

/**
 * Load user data from Firestore
 */
export async function loadUserData(userId: string): Promise<UserData> {
  const userDoc = await getDoc(doc(db, 'users', userId));

  if (userDoc.exists()) {
    return userDoc.data() as UserData;
  } else {
    // Create default user document
    const defaultData: UserData = {
      pasienterSykehjem: [],
      pasienterBolig: []
    };
    await setDoc(doc(db, 'users', userId), defaultData);
    return defaultData;
  }
}

/**
 * Save user data to Firestore
 */
export async function saveUserData(userId: string, data: Partial<UserData>): Promise<void> {
  await setDoc(doc(db, 'users', userId), data, { merge: true });
}

/**
 * Get authentication error message in Norwegian
 */
export function getAuthErrorMessage(errorCode: string): string {
  const messages: Record<string, string> = {
    'auth/user-not-found': 'Bruker ikke funnet',
    'auth/wrong-password': 'Feil passord',
    'auth/invalid-email': 'Ugyldig e-postadresse',
    'auth/user-disabled': 'Brukeren er deaktivert',
    'auth/too-many-requests': 'For mange forsøk. Prøv igjen senere',
    'auth/network-request-failed': 'Nettverksfeil. Sjekk internettforbindelsen',
    'auth/invalid-credential': 'Ugyldig påloggingsinformasjon'
  };
  return messages[errorCode] || 'Feil ved innlogging. Prøv igjen.';
}
